import React, { FC, useContext } from 'react';
import { Box, Card, Heading, Text, Flex } from '@resideo/blueprint-react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import ShippingOrderItem from './shipping/ShippingOrderItem';
import AddPromoCode from 'components/common/AddPromoCode';
import { convertMoneyAmount } from 'routes/ServicesAndSubscriptions/Cart';
import SeparatorLine from '../common/SeparatorLine';
import { fromId } from '@resideo/node-encoder/src';
import { CartContext } from '../../routes/ServicesAndSubscriptions/CartProvider';

const Container = styled(Card)`
  border: 2px solid ${({ theme }) => theme.colors.collapseBorder};
  border-radius: 0.9375rem;
  background-color: #fff;
`;

const RefText = styled(Text)`
  color: ${({ theme }) => theme.colors.summaryParagraph};
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: bold;
  padding: ${({ theme }) => theme.space.small} 0;
  font-family: 'Roboto';
`;

const TotalText = styled(Heading)`
  color: ${({ theme }) => theme.colors.cartTextHeading};
  letter-spacing: normal;
  font-size: ${({ theme }) => theme.fontSizes.medium};
  text-align: right;
  font-weight: bold;
`;

const Promo = styled(Flex)`
  .show-x {
    display: none;
  }
  :hover {
    .show-x {
      position: absolute;
      display: block;
      cursor: pointer;
    }
  }
`;

interface OrderSummaryDesktopProps {
  setFieldValue?: any;
  values?: any;
}

const OrderSummaryDesktop: FC<OrderSummaryDesktopProps> = ({
  setFieldValue,
  values,
}) => {
  const { t } = useTranslation();

  const order =
    localStorage.getItem('checkoutDetails') &&
    localStorage.getItem('checkoutDetails') !== 'undefined'
      ? JSON.parse(localStorage.getItem('checkoutDetails') || '')
      : {};

  const { state } = useContext(CartContext);

  const hasProduct =
    state?.cartItems?.filter(f => f.type === 'product').length > 0;

  const calculateTax = () => {
    // only include shipping cost in the tax calculation if there is
    // a physical product to be shipped.
    const shippingTotal = hasProduct
      ? values?.shippingMethod === 'standard'
        ? 10
        : 20
      : 0;
    const correctTaxAmount =
      (+order?.subTotal?.amount + shippingTotal) * +order?.taxRate;

    return correctTaxAmount;
  };
  let shippingTotal = 0;
  if (hasProduct)
    shippingTotal = values?.shippingMethod === 'standard' ? 10 : 20;

  let subTotal = 0;
  state.cartItems.map(res => {
    subTotal += parseFloat(res.orderPrice || 0) * parseFloat(res.quantity || 0);
  });

  const calculateTotal = +subTotal + shippingTotal + calculateTax();

  const [type, id] = fromId(order.id);

  return (
    <Container display={['none', 'none', 'none', 'block']} padding='large'>
      <Flex
        justifyContent='space-between'
        marginBottom='medium'
        alignItems={'center'}>
        <Box>
          <Heading color='textLight3' fontSize='1.125rem' fontFamily='Roboto'>
            {t('cart.shipping.orderSummary')}
          </Heading>
          {type === 'DigitalRiverOrder' ? (
            <RefText>REF # {order?.invoiceNumber}</RefText>
          ) : (
            <RefText>REF # {id}</RefText>
          )}
        </Box>
        <Box display={'flex'} justifyContent={'flex-end'} minWidth={'22.18rem'}>
          <AddPromoCode setFieldValue={setFieldValue} />
        </Box>
      </Flex>
      <SeparatorLine />
      {order &&
        order?.lineItems &&
        order?.lineItems.map(item => (
          <ShippingOrderItem data={item} key={item.sku} />
        ))}

      <Box data-test-shipping-form-sections>
        {values && values.promoCode && (
          <Promo paddingBottom='small'>
            <Box
              onClick={() => setFieldValue('promoCode', '')}
              className='show-x'>
              <Text textAlign='right'>
                <i className='fas fa-times'></i>{' '}
              </Text>
            </Box>
            <Box flex='75%'>
              <Text textAlign='right'>Promo Code </Text>
            </Box>
            <Box flex='25%'>
              <Text textAlign='right'>{values.promoCode}</Text>
            </Box>
          </Promo>
        )}
        {hasProduct && (
          <>
            <Flex paddingBottom='small'>
              <Box flex='75%'>
                <Text color='cartTextHeading' textAlign='right'>
                  {t('subtotalToday')}
                </Text>
              </Box>
              <Box flex='25%'>
                <Text color='cartTextHeading' textAlign='right'>
                  {convertMoneyAmount(
                    { amount: `${subTotal}`, currencyCode: 'USD' },
                    'en-us'
                  )}
                </Text>
              </Box>
            </Flex>

            <Flex paddingBottom='small'>
              <Box flex='75%'>
                <Text textAlign='right' color='cartTextHeading'>
                  {t('cart.shipping.shipping')}
                </Text>
              </Box>
              <Box flex='25%'>
                <Text textAlign='right' color='cartTextHeading'>
                  {convertMoneyAmount(
                    { amount: shippingTotal, currencyCode: order?.currency },
                    'en-us'
                  )}
                </Text>
              </Box>
            </Flex>
          </>
        )}

        <Flex paddingBottom='medium'>
          <Box flex='75%'>
            <Text textAlign='right' color='cartTextHeading'>
              {t('tax')} {t('notYetCalculated')}
            </Text>
          </Box>
          <Box flex='25%'>
            <Text textAlign='right' color='cartTextHeading'>
              {convertMoneyAmount(
                { amount: calculateTax(), currencyCode: order?.currency },
                'en-us'
              )}
            </Text>
          </Box>
        </Flex>
        <Flex>
          <Box flex='75%'>
            <TotalText color='cartTextHeading'>{t('totalToday')}</TotalText>
          </Box>
          <Box flex='25%'>
            <TotalText>
              {convertMoneyAmount(
                { amount: calculateTotal, currencyCode: order?.currency },
                'en-us'
              )}
            </TotalText>
          </Box>
        </Flex>
      </Box>
    </Container>
  );
};

export default OrderSummaryDesktop;
